/**
 * How fast an aircraft's propeller is turning, as a fraction of its
 * full-throttle rate — the one signal AD-012's disc is driven by.
 *
 * **Deliberately the *same* signal the engine audio runs on.** AD-012 §3: the
 * disc reads `audio/engine-voice.ts`'s `engineTargetIndex(throttle,
 * airspeedMps)` — the measured OPL2 pitch law plus its measured windmilling
 * term — rather than inventing a second RPM model, so what the player sees and
 * what he hears can never disagree. The index is a *pitch* index, so it is
 * mapped back onto the crank RPM band `continuous-params.ts` already names
 * (`ENGINE_IDLE_RPM`/`ENGINE_FULL_RPM`, a Clerget 9B's own 650-1250 rev/min)
 * before being normalised: an engine at idle is turning at half its full rate,
 * not at zero, and a disc that showed stopped blades on a closed throttle
 * would be wrong in exactly the way the near-stopped regime exists to be right.
 *
 * **A dead engine windmills, it does not stop dead.** When no power is being
 * delivered at all — engine shot out, fuel exhausted, carburettor iced, pilot
 * dead — the propeller is turned by the airstream alone. That is the same
 * `windmillIndexSteps` airspeed band, scaled to `WINDMILL_DEAD_FRACTION` of
 * full rate, so a stricken aeroplane's prop slows to a visible rotation and a
 * wreck sitting still shows blades that have stopped. INFERRED, port-side: the
 * original has no propeller at all to be faithful to.
 *
 * Purely a read of `SimState` — nothing here writes, hashes or steps anything.
 */

import { ENGINE_FULL_RPM, ENGINE_IDLE_RPM } from "../audio/continuous-params.ts";
import {
  ENGINE_INDEX_MAX,
  ENGINE_INDEX_MIN,
  engineTargetIndex,
  windmillIndexSteps,
  WINDMILL_MAX_STEPS,
} from "../audio/engine-voice.ts";

/** What a stricken aeroplane's propeller reaches at dive speed, as a fraction of full rate. */
export const WINDMILL_DEAD_FRACTION = 0.35;

/**
 * Below this fraction the disc gives way to visible blade silhouettes
 * (AD-012 §2's "near-stopped" regime). Crossed on start-up, on shutdown and by
 * a windmilling propeller below roughly cruise speed.
 */
export const BLADE_REGIME_BELOW = 0.12;

/** Above this fraction the shimmer disc is at full strength; between the two the regimes cross-fade. */
export const DISC_REGIME_ABOVE = 0.3;

/** The fields of a `SimState` this reads — spelled out so a test can build one by hand. */
export interface PropellerDrive {
  /** `SimState.engine.throttle` — the smoothed setting actually reaching the engine. */
  readonly throttle: number;
  /** `SimState.engine.deliveredPowerW`; zero means the engine is making no power at all. */
  readonly deliveredPowerW: number;
  /** `SimState.readout.airspeedMps`. */
  readonly airspeedMps: number;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** The windmilling-only rate for a propeller with no power behind it. */
function windmillFraction(airspeedMps: number): number {
  return WINDMILL_DEAD_FRACTION * clamp01(windmillIndexSteps(airspeedMps) / WINDMILL_MAX_STEPS);
}

/** 0..1 propeller rate: 1 is full throttle, 0 is stopped. */
export function propellerRpmFraction(drive: PropellerDrive): number {
  if (!(drive.deliveredPowerW > 0)) return windmillFraction(drive.airspeedMps);
  const index = engineTargetIndex(clamp01(drive.throttle), drive.airspeedMps);
  const indexFraction = clamp01((index - ENGINE_INDEX_MIN) / (ENGINE_INDEX_MAX - ENGINE_INDEX_MIN));
  return clamp01((ENGINE_IDLE_RPM + (ENGINE_FULL_RPM - ENGINE_IDLE_RPM) * indexFraction) / ENGINE_FULL_RPM);
}

/**
 * How much of the shimmer disc (1) versus the blade silhouettes (0) a given
 * rate shows. A smooth crossfade rather than a switch, so spooling up never
 * pops from one regime to the other.
 */
export function discRegimeMix(rpmFraction: number): number {
  const t = clamp01((rpmFraction - BLADE_REGIME_BELOW) / (DISC_REGIME_ABOVE - BLADE_REGIME_BELOW));
  return t * t * (3 - 2 * t);
}

/**
 * Revolutions per second the *drawn* propeller turns at.
 *
 * **Not the real rate, and that is the whole point.** A Clerget at full
 * throttle turns 1250 rev/min — 20.8 rev/s — and a two-bladed pattern spun at
 * 20.8 rev/s and sampled at a 60 Hz display strobes violently: that beat *is*
 * the "flicker" AD-012 §2 forbids, and no amount of shader work removes it
 * because it is a sampling artefact of the rate itself. The disc's banding is
 * therefore turned at a rate chosen to alias against nothing: at most
 * `DISC_TURN_REV_PER_S` revolutions a second, which with the shader's three
 * lobes puts the fastest on-screen pattern at 4.8 Hz — a twelfth of the
 * display rate, comfortably inside where rotation reads as rotation.
 *
 * The blade regime is different and needs no such treatment: a near-stopped
 * propeller really is turning slowly, so its silhouettes turn at the true rate
 * (`BLADE_TURN_REV_PER_S` at the top of the regime, which is well under 1
 * rev/s) and a stopped one really does stop.
 */
export const DISC_TURN_REV_PER_S = 1.6;
export const BLADE_TURN_REV_PER_S = 6;

/** Radians the drawn propeller advances in `dtS` at a given rate — the integrated phase both regimes share. */
export function propellerPhaseStep(rpmFraction: number, dtS: number): number {
  const mix = discRegimeMix(rpmFraction);
  const revPerS = BLADE_TURN_REV_PER_S * rpmFraction * (1 - mix) + DISC_TURN_REV_PER_S * rpmFraction * mix;
  return revPerS * Math.max(0, dtS) * Math.PI * 2;
}
